import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { PURCHASE_POINTS_CACHE_CONFIG } from "@/features/purchasePoints/config/purchasePoints.config";
import { toPurchasePointsError } from "@/features/purchasePoints/errors/purchasePointsError";

// Where the employee sits in the query key. The `placeholderData` guard reads
// it back off the previous query: moving it without updating this constant
// would drop the isolation between accounts without breaking a single type.
const EMPLOYEE_KEY_INDEX = 1;

type UseEmployeeScopedQueryOptions<TData> = {
  scope: string
  // What tells two queries of the same employee apart, pagination for instance.
  key?: readonly unknown[]
  queryFn: () => Promise<TData>
  enabled?: boolean
};

/**
 * The policy shared by the purchase points lists: the query is personal, silent
 * to the global error handler, and never serves another account's data.
 */
export const useEmployeeScopedQuery = <TData>({
  scope,
  key = [],
  queryFn,
  enabled = true,
}: UseEmployeeScopedQueryOptions<TData>) => {
  const { user } = useAuth();
  const employeeId = user?.employee?.id ?? null;
  const isQueryEnabled = enabled && !!employeeId;

  const {
    data, isPending, isFetching, error, refetch,
  } = useQuery({
    // The employee id isolates the catalogue: this data is personal and must
    // never be served again to another account.
    queryKey: [scope, employeeId, ...key],
    queryFn,
    enabled: isQueryEnabled,
    // Every error code already has its own state in the page: letting the
    // global handler also surface the backend message would duplicate the
    // information and step outside the feature's wording.
    meta: { errorConfig: { silent: true } },
    // Keeping the previous page avoids flickering the grid, but only at
    // constant identity: otherwise the previous account's data would stay
    // visible until the new response lands.
    placeholderData: (previousData, previousQuery) => (
      previousQuery?.queryKey[EMPLOYEE_KEY_INDEX] === employeeId ? previousData : undefined
    ),
    ...PURCHASE_POINTS_CACHE_CONFIG,
  });

  return {
    data,
    // A disabled query stays "pending" forever: with no attached employee, or
    // when the caller switches it off, announcing a load would freeze the
    // screen on its skeleton.
    isLoading: isPending && isQueryEnabled,
    // No request was ever sent: neither a failure nor an empty result can be
    // claimed, and the caller has to render neither.
    isDisabled: !isQueryEnabled,
    isFetching,
    error: error ? toPurchasePointsError(error) : null,
    retry: refetch,
  };
};
